import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export const ImageCardDropdownSizeRange = ({
  sizeRanges,
  selectedSizeRanges,
  setSelectedSizeRanges,
  sizeRangeSelectRef,
  className = "",
  fitContent = false,
  dropdownContainerRef = null,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(null);

  const selectableSizeRanges = useMemo(
    () => (sizeRanges || []).filter((range) => range !== "total"),
    [sizeRanges],
  );

  const listOptions = useMemo(() => {
    const base = selectableSizeRanges.map((range) => ({
      value: range,
      name: `${range}m`,
    }));
    return [
      {
        value: "all",
        name: t("All sizes"),
      },
      ...base,
    ];
  }, [selectableSizeRanges, t]);

  const allValues = useMemo(
    () => selectableSizeRanges.map((r) => r.toLowerCase()),
    [selectableSizeRanges],
  );

  const isAllSelected = useMemo(() => {
    if (!selectedSizeRanges) return false;
    return allValues.every((v) => selectedSizeRanges.has(v));
  }, [selectedSizeRanges, allValues]);

  const selectedValues = useMemo(() => {
    if (!selectedSizeRanges) return [];
    if (isAllSelected) {
      const allOption = listOptions.find((opt) => opt.value === "all");
      const individual = listOptions.filter((opt) => opt.value !== "all");
      return [allOption, ...individual];
    }
    return listOptions.filter(
      (opt) =>
        opt.value !== "all" && selectedSizeRanges.has(opt.value.toLowerCase()),
    );
  }, [selectedSizeRanges, listOptions, isAllSelected]);

  const updateSelected = useCallback(
    (selectedList) => {
      const values = selectedList.map((item) => item.value);
      const selectedRangeValues = values
        .filter((v) => v !== "all")
        .map((v) => String(v).toLowerCase());

      // If we are in "all" state and user toggles the "all" option off
      if (
        isAllSelected &&
        !values.includes("all") &&
        selectedRangeValues.length === allValues.length
      ) {
        setSelectedSizeRanges(new Set(["total"]));
        return;
      }

      // "All sizes" behaves like "all materials": toggle select all / clear all.
      if (values.includes("all")) {
        const allStillSelected =
          selectedRangeValues.length === allValues.length &&
          allValues.every((val) => selectedRangeValues.includes(val));

        // If we were already in "all" state, clicking "all" again clears all.
        if (isAllSelected && allStillSelected) {
          setSelectedSizeRanges(new Set(["total"]));
          return;
        }

        // Otherwise select everything.
        setSelectedSizeRanges(new Set([...allValues, "total"]));
        return;
      }

      // If user manually selected all ranges, treat it as "all".
      const allIndividuallySelected =
        selectedRangeValues.length === allValues.length &&
        allValues.every((val) => selectedRangeValues.includes(val));
      if (allIndividuallySelected) {
        setSelectedSizeRanges(new Set([...allValues, "total"]));
        return;
      }

      if (selectedRangeValues.length === 0) {
        setSelectedSizeRanges(new Set(["total"]));
        return;
      }

      setSelectedSizeRanges(new Set([...selectedRangeValues, "total"]));
    },
    [setSelectedSizeRanges, allValues, isAllSelected],
  );

  // Get display text
  const displayText = useMemo(() => {
    if (selectedSizeRanges.size === 0) {
      return t("select_size_ranges");
    }
    if (isAllSelected) {
      return t("All sizes");
    }
    return t("size_ranges_selected", {
      count: Math.max(
        0,
        selectedSizeRanges.size - (selectedSizeRanges.has("total") ? 1 : 0),
      ),
    });
  }, [selectedSizeRanges, isAllSelected, t]);

  // Measure button width when it changes
  useEffect(() => {
    if (buttonRef.current) {
      const updateWidth = () => {
        if (buttonRef.current) {
          setButtonWidth(buttonRef.current.offsetWidth);
        }
      };
      updateWidth();
      // Update on window resize
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [displayText, isOpen]);

  // Close dropdown when clicking outside (include portaled dropdown content when dropdownContainerRef is used)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target) ?? false;
      const inPortal =
        dropdownContainerRef?.current?.contains(event.target) ?? false;
      if (!inTrigger && !inPortal) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, dropdownContainerRef]);

  // Expose reset method to ref
  useEffect(() => {
    if (sizeRangeSelectRef) {
      const resetMethod = {
        resetSelectedValues: () => {
          setSelectedSizeRanges(new Set(allValues));
          setIsOpen(false);
        },
      };

      if (typeof sizeRangeSelectRef === "function") {
        sizeRangeSelectRef(resetMethod);
      } else if (sizeRangeSelectRef && typeof sizeRangeSelectRef === "object") {
        sizeRangeSelectRef.current = resetMethod;
      }
    }
  }, [sizeRangeSelectRef, setSelectedSizeRanges, allValues]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectOption = (option) => {
    const currentSelected = selectedValues.map((v) => v.value);
    const isCurrentlySelected = currentSelected.includes(option.value);

    let newSelected;
    if (option.value === "all") {
      if (isAllSelected) {
        // Uncheck "all": uncheck all checkboxes
        newSelected = [];
      } else {
        // Check "all": check all individual checkboxes
        newSelected = listOptions.filter((opt) => opt.value !== "all");
      }
    } else {
      // Handle individual checkboxes
      if (isCurrentlySelected) {
        // Uncheck an individual checkbox
        newSelected = selectedValues.filter((v) => v.value !== option.value);
        // If "all" was in the selection, remove it too
        newSelected = newSelected.filter((v) => v.value !== "all");
      } else {
        // Check an individual checkbox
        newSelected = [...selectedValues, option];
        // Remove "all" from selection if it was present
        newSelected = newSelected.filter((v) => v.value !== "all");
        // If all individual checkboxes are now checked, also check "all"
        const individualSelected = newSelected;
        if (individualSelected.length === allValues.length) {
          const allOption = listOptions.find((opt) => opt.value === "all");
          if (allOption) {
            newSelected = [allOption, ...individualSelected];
          }
        }
      }
    }

    updateSelected(newSelected);
  };

  return (
    <div
      className={`relative bg-white rounded-full ${
        fitContent ? "w-auto min-w-0" : "flex-1 min-w-[200px]"
      } ${className}`}
      ref={dropdownRef}
    >
      {/* Custom styled button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={`flex items-center justify-between bg-transparent outline-none cursor-pointer py-1.5 px-2 h-8 text-left ${
          fitContent ? "w-auto" : "w-full"
        }`}
      >
        <span
          className={`text-gray-900 font-medium ${
            fitContent ? "whitespace-nowrap" : "truncate flex-1"
          }`}
        >
          {displayText}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${
            isOpen ? "" : "transform rotate-180"
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu - portaled to dropdownContainerRef when provided so left edge aligns with full "Taille :" block */}
      {isOpen &&
        (dropdownContainerRef?.current ? (
          createPortal(
            <div
              className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-[100] max-h-64 overflow-y-auto"
              style={{
                width: buttonWidth ? `${Math.max(buttonWidth, 280)}px` : "100%",
                minWidth: "280px",
              }}
            >
              <div className="py-1">
                {listOptions.map((option, index) => {
                  const isSelected = selectedValues.some(
                    (v) => v.value === option.value,
                  );
                  return (
                    <div
                      key={option.value || index}
                      onClick={() => handleSelectOption(option)}
                      className={`group flex items-center px-2 py-2.5 cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-gray-50 hover:bg-[#2AB9B8]"
                          : "hover:bg-[#2AB9B8]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="w-4 h-4 mr-2 rounded focus:ring-[#2AB9B8] cursor-pointer flex-shrink-0 border-gray-300 text-[#2AB9B8] group-hover:border-white"
                        style={{ accentColor: "#2AB9B8" }}
                      />
                      <span className="flex-1 text-sm font-medium text-gray-900 group-hover:text-white">
                        {option.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>,
            dropdownContainerRef.current,
          )
        ) : (
          <div
            className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-[100] max-h-64 overflow-y-auto"
            style={{
              width: buttonWidth ? `${Math.max(buttonWidth, 280)}px` : "100%",
              minWidth: "280px",
            }}
          >
            <div className="py-1">
              {listOptions.map((option, index) => {
                const isSelected = selectedValues.some(
                  (v) => v.value === option.value,
                );
                return (
                  <div
                    key={option.value || index}
                    onClick={() => handleSelectOption(option)}
                    className={`group flex items-center px-2 py-2.5 cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-gray-50 hover:bg-[#2AB9B8]"
                        : "hover:bg-[#2AB9B8]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-4 h-4 mr-2 rounded focus:ring-[#2AB9B8] cursor-pointer flex-shrink-0 border-gray-300 text-[#2AB9B8] group-hover:border-white"
                      style={{ accentColor: "#2AB9B8" }}
                    />
                    <span className="flex-1 text-sm font-medium text-gray-900 group-hover:text-white">
                      {option.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
    </div>
  );
};
